import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, MessageCircle, Mail, Globe, Printer } from "lucide-react";
import api from "@/lib/api";
import { usePageMeta, useNoIndex } from "@/lib/seo";

export default function PaymentResult() {
  usePageMeta("Payment Status", "Confirming your payment with Makchester Tours & Travel.");
  useNoIndex();
  const [params] = useSearchParams();
  const token = params.get("token");
  const cancelled = params.get("cancelled") === "1";
  const failed = params.get("failed") === "1";
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!token) { setStatus("error"); return; }
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${token}`);
        setInfo(data);
        if (data.request_status === "paid" || data.transaction_status === "Paid") { setStatus("paid"); return; }
        if (data.transaction_status === "Failed" || failed) { setStatus("failed"); return; }
        if (cancelled) { setStatus("cancelled"); return; }
        tries.current += 1;
        if (tries.current < 20 && !stop) setTimeout(poll, 3000);
        else setStatus("pending_timeout");
      } catch (e) {
        if (e.response?.status === 429 && tries.current < 20 && !stop) {
          tries.current += 1;
          setTimeout(poll, 5000);
          return;
        }
        setStatus("error");
      }
    };
    poll();
    return () => { stop = true; };
  }, [token, cancelled, failed]);

  if (status === "paid" && info) return <SuccessReceipt info={info} />;

  const views = {
    checking: { icon: Clock, color: "text-[#D4A62A]", title: "Confirming your payment…", text: "Please wait a moment while we verify your payment with the bank. Do not close this page.", spin: true },
    failed: { icon: XCircle, color: "text-red-500", title: "Payment unsuccessful", text: "Your payment was not completed. No successful payment has been recorded. Please try again or contact Makchester for assistance." },
    cancelled: { icon: XCircle, color: "text-amber-500", title: "Payment cancelled", text: "Your payment was not completed. No successful payment has been recorded. Your payment link remains valid — you can try again anytime, or contact Makchester for assistance." },
    pending_timeout: { icon: Clock, color: "text-[#D4A62A]", title: "Still confirming…", text: "Your payment is taking a little longer to confirm. Don't worry — if it succeeded you'll receive an email receipt shortly. Contact us if you're unsure." },
    error: { icon: XCircle, color: "text-red-500", title: "Unable to check status", text: "We couldn't retrieve the payment status. If you completed payment, you'll receive an email receipt. Please contact us if unsure." },
  };
  const v = views[status] || views.checking;

  return (
    <div className="bg-[#0A2E4D] min-h-screen py-24 px-4">
      <div className="max-w-md mx-auto bg-white rounded-2xl p-10 text-center shadow-2xl" data-testid="payment-result-card">
        <v.icon size={44} className={`mx-auto ${v.color} ${v.spin ? "animate-pulse" : ""}`} strokeWidth={1.4} />
        <h1 className="font-serif-display text-2xl text-[#0A2E4D] mt-5" data-testid="payment-result-title">{v.title}</h1>
        <p className="text-sm text-[#4B5563] mt-3 leading-relaxed">{v.text}</p>
        <div className="flex flex-wrap justify-center gap-3 mt-7">
          {(status === "failed" || status === "cancelled") && token && (
            <Link to={`/pay/${token}`} className="inline-flex items-center bg-[#D4A62A] hover:bg-[#BA901D] text-white px-6 py-3 rounded-full text-xs font-bold uppercase tracking-wider" data-testid="retry-payment-btn">Try Again</Link>
          )}
          <Link to="/" className="inline-flex items-center bg-[#0A2E4D] text-white px-6 py-3 rounded-full text-xs font-bold uppercase tracking-wider">Back to Website</Link>
          <a href="https://wa.me/971559209503" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-[#25D366] text-white px-6 py-3 rounded-full text-xs font-bold uppercase tracking-wider"><MessageCircle size={13} /> WhatsApp</a>
        </div>
      </div>
    </div>
  );
}

function SuccessReceipt({ info }) {
  const paidDate = info.paid_at
    ? new Date(info.paid_at).toLocaleString("en-GB", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : new Date().toLocaleString("en-GB", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  return (
    <div className="bg-[#0A2E4D] min-h-screen py-16 px-4 print:bg-white print:py-4">
      <div className="max-w-lg mx-auto bg-white rounded-2xl overflow-hidden shadow-2xl print:shadow-none" data-testid="payment-success-receipt">
        <div className="bg-[#0A2E4D] px-8 py-6 print:bg-white">
          <img src="/logo-white.png" alt="Makchester Tours & Travel" className="h-11 w-auto print:hidden" />
          <img src="/logo-color.png" alt="Makchester Tours & Travel" className="h-11 w-auto hidden print:block" />
          <div className="text-[#E3BA45] text-[10px] font-bold uppercase tracking-[0.25em] mt-2">Your Gateway to the World</div>
        </div>
        <div className="px-8 py-7">
          {info.test_mode && <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-bold uppercase tracking-wider px-3 py-2 rounded-lg text-center" data-testid="receipt-test-badge">Test mode — no real charge was made</div>}
          <div className="text-center">
            <CheckCircle2 size={46} className="mx-auto text-emerald-500" strokeWidth={1.4} />
            <h1 className="font-serif-display text-3xl text-[#0A2E4D] mt-3" data-testid="receipt-title">Payment Confirmed</h1>
          </div>
          <dl className="mt-6 space-y-2.5 text-sm border border-[#E5E7EB] rounded-xl p-5">
            <RRow label="Customer" value={info.customer_name} testid="receipt-customer" />
            <RRow label="Payment reference" value={info.payment_ref} testid="receipt-ref" />
            <RRow label="Amount paid" value={`${info.currency} ${Number(info.amount_aed).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} testid="receipt-amount" />
            <RRow label="Date & time" value={paidDate} />
            <RRow label="Service" value={info.service} />
            <RRow label="Payment method" value={info.payment_method} />
            <RRow label="Status" value="Paid" green testid="receipt-status" />
          </dl>
          <div className="mt-5 space-y-1.5 text-xs text-[#4B5563]">
            <p className="flex items-start gap-2"><Mail size={13} className="text-[#D4A62A] mt-0.5 shrink-0" /> A receipt has been emailed to you.</p>
            <p className="flex items-start gap-2"><CheckCircle2 size={13} className="text-[#D4A62A] mt-0.5 shrink-0" /> A Makchester consultant has been notified of your payment.</p>
          </div>
          <p className="mt-5 text-[13px] text-[#374151] leading-relaxed bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-4">
            Thank you for your payment. Your booking or service request is now being processed by Makchester Tours &amp; Travel. A consultant will contact you if any further information is required.
          </p>
          <div className="flex flex-wrap gap-2.5 mt-6 print:hidden">
            <Link to="/account" className="inline-flex items-center bg-[#D4A62A] hover:bg-[#BA901D] text-white px-5 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider" data-testid="receipt-view-booking-btn">View My Booking / Enquiry</Link>
            <Link to="/" className="inline-flex items-center bg-[#0A2E4D] text-white px-5 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider">Back to Website</Link>
            <a href="https://wa.me/971559209503" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-[#25D366] text-white px-5 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider"><MessageCircle size={12} /> WhatsApp</a>
            <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 border border-[#0A2E4D] text-[#0A2E4D] px-5 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider" data-testid="receipt-print-btn"><Printer size={12} /> Download Receipt</button>
          </div>
          <div className="mt-7 pt-5 border-t border-[#E5E7EB] text-center text-xs text-[#4B5563]">
            <div className="flex flex-wrap justify-center gap-4">
              <a href="mailto:explore@makchester.com" className="inline-flex items-center gap-1 text-[#0A2E4D] hover:text-[#BA901D]"><Mail size={12} /> explore@makchester.com</a>
              <a href="https://wa.me/971559209503" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0A2E4D] hover:text-[#BA901D]"><MessageCircle size={12} /> +971 55 920 9503</a>
              <a href="https://www.makchester.com" className="inline-flex items-center gap-1 text-[#0A2E4D] hover:text-[#BA901D]"><Globe size={12} /> www.makchester.com</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RRow({ label, value, green, testid }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-[#4B5563]">{label}</dt>
      <dd className={`font-semibold text-right ${green ? "text-emerald-600" : "text-[#0A2E4D]"}`} data-testid={testid}>{value}</dd>
    </div>
  );
}
